<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'

import utils from '@/utils.js'
import consts from '@/consts.js'

const globalStore = useGlobalStore()

const videoElement = ref(null)
const imageElement = ref(null)

let unsubscribes

onMounted(() => {
  state.imageUrl = imgproxyUrl(props.image, props.width, props.height)
  window.addEventListener('mousemove', updateCanvasSelectedClass)
  window.addEventListener('touchmove', updateCanvasSelectedClass)
  window.addEventListener('focus', updateIsPlaying)
  window.addEventListener('blur', updateIsPlaying)
  document.addEventListener('visibilitychange', updateIsPlaying)

  const globalActionUnsubscribe = globalStore.$onAction(
    async ({ name, args }) => {
      if (name === 'triggerUploadComplete') {
        const { url, fileName } = args[0]
        if (props.video.includes(fileName)) {
          await nextTick()
          videoElement.value.load()
        }
      }
    }
  )
  unsubscribes = () => {
    globalActionUnsubscribe()
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', updateCanvasSelectedClass)
  window.removeEventListener('touchmove', updateCanvasSelectedClass)
  window.removeEventListener('focus', updateIsPlaying)
  window.removeEventListener('blur', updateIsPlaying)
  document.removeEventListener('visibilitychange', updateIsPlaying)
  unsubscribes()
})

const emit = defineEmits(['loadSuccess'])

const props = defineProps({
  isSelectedOrDragging: Boolean,
  pendingUploadDataUrl: String,
  image: String,
  video: String,
  videoIsPaused: Boolean,
  cardId: String,
  width: Number,
  height: Number
})

const state = reactive({
  imageUrl: null,
  imageProxySrcSet: null
})

const isTouching = computed(() => globalStore.isPinchZooming || globalStore.isTouchScrolling)

watch(() => props.image, (url) => {
  if (!url && !props.pendingUploadDataUrl) {
    state.imageUrl = null
    state.imageProxySrcSet = null
  }
  const onLoaded = () => {
    state.imageUrl = imgproxyUrl(url, props.width, props.height)
  }
  const image = new Image()
  image.addEventListener('load', onLoaded)
  image.addEventListener('error', handleError)
  image.src = url
  if (image.complete) {
    onLoaded()
  }
})
watch(() => props.pendingUploadDataUrl, (url) => {
  if (url) {
    state.imageUrl = url
  }
})
watch(() => props.width, (width) => {
  state.imageUrl = imgproxyUrl(props.image, props.width, props.height)
})
watch(() => props.height, (height) => {
  state.imageUrl = imgproxyUrl(props.image, props.width, props.height)
})
watch(() => globalStore.multipleSelectedActionsIsVisible, (value) => {
  if (value) { return }
  removeCanvasSelectedClass()
})

const lazyLoading = computed(() => {
  if (globalStore.disableViewportOptimizations) {
    return 'eager' // default
  } else {
    return 'lazy'
  }
})

// pause when item details dialog is visible

const handleItemDetailsDialogIsVisible = (value) => {
  if (value) {
    pause()
  } else {
    play()
  }
}
watch(() => globalStore.cardDetailsIsVisibleForCardId, (value) => {
  handleItemDetailsDialogIsVisible(value)
})
watch(() => globalStore.connectionDetailsIsVisibleForConnectionId, (value) => {
  handleItemDetailsDialogIsVisible(value)
})
watch(() => globalStore.boxDetailsIsVisibleForBoxId, (value) => {
  handleItemDetailsDialogIsVisible(value)
})
watch(() => globalStore.listDetailsIsVisibleForListId, (value) => {
  handleItemDetailsDialogIsVisible(value)
})
watch(() => globalStore.lineDetailsIsVisibleForLineId, (value) => {
  handleItemDetailsDialogIsVisible(value)
})

// video

watch(() => props.videoIsPaused, (value) => {
  if (!props.video) { return }
  if (value) {
    pauseVideo()
  } else {
    playVideo()
  }
})
const pause = () => {
  pauseVideo()
  pauseGif()
}
const play = () => {
  playVideo()
  playGif()
}
const pauseVideo = () => {
  if (!props.video) { return }
  const element = videoElement.value
  element.pause()
}
const playVideo = () => {
  if (!props.video) { return }
  if (props.videoIsPaused) { return }
  const element = videoElement.value
  element.play()
}

// gif

watch(() => isTouching.value, (value) => {
  if (value) {
    pause()
  } else {
    play()
  }
})
const imageIsGif = computed(() => {
  const url = state.imageUrl
  if (!url) { return }
  return url.includes('.gif')
})
const updateIsPlaying = () => {
  // pause gifs and videos while the window or tab is inactive to reduce idle cpu and gpu use
  const windowIsActive = !document.hidden && document.hasFocus()
  if (windowIsActive) {
    play()
  } else {
    pause()
  }
}
const pauseGif = () => {
  // adapted from https://stackoverflow.com/a/24707088
  // create canvas element from first frame of video
  if (globalStore.disableViewportOptimizations) { return }
  if (!imageIsGif.value) { return }
  if (canvasElement()) { return } // already paused
  const image = imageElement.value
  const width = image.width
  const height = image.height
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d').drawImage(image, 0, 0, width, height)
  let attr
  let i = 0
  for (i = 0; i < image.attributes.length; i++) {
    attr = image.attributes[i]
    if (attr.name !== '"') { // test for invalid attributes
      canvas.setAttribute(attr.name, attr.value)
    }
  }
  canvas.style.position = 'absolute'
  canvas.classList.add('pause')
  image.parentNode.insertBefore(canvas, image)
  image.style.opacity = 0
}
const canvasElement = () => {
  const canvasElement = imageElement.value?.previousElementSibling
  const isCanvas = canvasElement?.nodeName === 'CANVAS'
  if (!isCanvas) { return }
  return canvasElement
}
const playGif = () => {
  // remove pause canvas
  if (globalStore.disableViewportOptimizations) { return }
  if (!imageIsGif.value) { return }
  const canvas = canvasElement()
  if (!canvas) { return }
  canvas.remove()
  imageElement.value.style.opacity = 1
}
const updateCanvasSelectedClass = () => {
  if (!globalStore.currentUserIsPaintSelecting) { return }
  const canvas = canvasElement()
  if (!canvas) { return }
  const multipleCardsSelectedIds = globalStore.multipleCardsSelectedIds
  const isSelected = multipleCardsSelectedIds.includes(props.cardId)
  if (!isSelected) { return }
  canvas.classList.add('selected')
}
const removeCanvasSelectedClass = () => {
  const canvas = canvasElement()
  if (!canvas) { return }
  canvas.classList.remove('selected')
}

// serve smaller images w imgproxy

const imgproxyUrl = (imageUrl, width, height) => {
  if (props.pendingUploadDataUrl) {
    return props.pendingUploadDataUrl
  }
  const containerBreakpoints = [400, 600, 800, 1200, 3000]
  const devicePixelRatio = Math.round(window.devicePixelRatio || 1)
  const maxDimensions = Math.max(width, height)
  let url = utils.imgproxyUrl(imageUrl)
  for (const breakpoint of containerBreakpoints) {
    if (maxDimensions <= breakpoint) {
      url = utils.imgproxyUrl(imageUrl, breakpoint * devicePixelRatio)
    }
  }
  return url
}

// events

const handleSuccess = (event) => {
  emit('loadSuccess')
}
const handleError = (event) => {
}
</script>

<template lang="pug">
//- Video
video(v-if="Boolean(props.video)" :autoplay="!props.videoIsPaused" loop muted playsinline :key="props.video" :class="{selected: isSelectedOrDragging}" @canplay="handleSuccess" ref="videoElement" @load="handleSuccess")
  source(:src="props.video")
//- Image
img.image(
  v-if="state.imageUrl"
  ref="imageElement"
  :src="state.imageUrl"
  :class="{selected: isSelectedOrDragging}"
  @load="handleSuccess"
  @error="handleError"
  :loading="lazyLoading"
)
</template>

<style lang="stylus">
.media-card
  .image,
  video
    border-radius var(--entity-radius)
    display block
    -webkit-touch-callout none // prevents safari mobile press-and-hold from interrupting
    content-visibility auto
    &.selected
      mix-blend-mode color-burn
</style>
